import { useEffect, useState } from "react";
import { Outlet, useParams, useNavigate, Link, useOutletContext } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, User } from "lucide-react";
import { storeApi } from "./api";
import { useBag } from "./useBag";
import { useCustomer } from "./useCustomer";
import BagDrawer from "./BagDrawer";

export function useStorefront() {
  return useOutletContext();
}

export default function StorefrontLayout() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const openBag = useBag((s) => s.openBag);
  const bagCount = useBag((s) => s.items.reduce((a, i) => a + i.quantity, 0));
  const isAuthed = useCustomer((s) => s.isAuthed);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([storeApi.getStore(code), storeApi.getCategories(code)])
      .then(([s, c]) => {
        if (!active) return;
        setStore(s);
        setCategories(c);
      })
      .catch(() => active && setStore(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [code]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/store/${code}/search?q=${encodeURIComponent(query.trim())}`);
      setSearchOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="h-6 w-6 rounded-full border-2 border-black border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-black font-[Manrope]">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">404</p>
        <h1 className="font-[Bodoni_Moda] text-4xl mt-4">Store not found</h1>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black font-[Manrope] antialiased flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md">
        <div className="px-6 md:px-12 lg:px-24 h-20 flex items-center justify-between">
          <button className="md:hidden p-1" onClick={() => setMenuOpen(true)} data-testid="store-menu-open">
            <Menu className="h-5 w-5" />
          </button>

          <nav className="hidden md:flex items-center gap-6 lg:gap-8 max-w-[38%] overflow-hidden">
            {categories.slice(0, 4).map((c) => (
              <Link
                key={c.id}
                to={`/store/${code}/c/${c.id}`}
                data-testid={`store-nav-cat-${c.id}`}
                className="text-[11px] uppercase tracking-[0.15em] font-medium text-zinc-600 hover:text-black transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </nav>

          <Link
            to={`/store/${code}`}
            className="absolute left-1/2 -translate-x-1/2 font-[Bodoni_Moda] text-2xl md:text-3xl tracking-tight"
            data-testid="store-logo"
          >
            {store.name}
          </Link>

          <div className="flex items-center gap-5">
            <button onClick={() => setSearchOpen((v) => !v)} data-testid="store-search-toggle">
              <Search className="h-5 w-5" />
            </button>
            <Link to={`/store/${code}/account`} className="relative" data-testid="store-account-link">
              <User className="h-5 w-5" />
              {isAuthed && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-black" />}
            </Link>
            <button onClick={openBag} className="relative" data-testid="store-bag-open">
              <ShoppingBag className="h-5 w-5" />
              {bagCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-black text-white text-[10px] h-4 min-w-4 px-1 rounded-full flex items-center justify-center">
                  {bagCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="px-6 md:px-12 lg:px-24 pb-5">
            <form onSubmit={submitSearch}>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="SEARCH"
                data-testid="store-search-input"
                className="w-full bg-transparent border-0 border-b border-zinc-300 focus:border-black focus:ring-0 px-0 py-3 text-sm tracking-[0.1em] uppercase placeholder:text-zinc-400 outline-none"
              />
            </form>
          </div>
        )}
      </header>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 bg-white md:hidden">
          <div className="h-20 px-6 flex items-center justify-between">
            <span className="font-[Bodoni_Moda] text-2xl">{store.name}</span>
            <button onClick={() => setMenuOpen(false)} data-testid="store-menu-close">
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="px-6 py-6 flex flex-col gap-6">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/store/${code}/c/${c.id}`}
                onClick={() => setMenuOpen(false)}
                className="text-sm uppercase tracking-[0.15em]"
              >
                {c.name}
              </Link>
            ))}
          </nav>
        </div>
      )}

      <main className="flex-1">
        <Outlet context={{ store, categories, code }} />
      </main>

      {/* Footer */}
      <footer className="mt-24 border-t border-zinc-200 px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2 md:col-span-1">
            <p className="font-[Bodoni_Moda] text-3xl">{store.name}</p>
            <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
              Considered clothing for the modern wardrobe. {store.city}.
            </p>
          </div>
          {[
            ["Shop", categories.slice(0, 4).map((c) => c.name)],
            ["Help", ["Shipping & Returns", "Size Guide", "Contact"]],
            ["Company", ["About", "Sustainability", "Stores"]],
          ].map(([title, links]) => (
            <div key={title}>
              <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 mb-4">{title}</p>
              <ul className="space-y-3">
                {links.map((l) => (
                  <li key={l} className="text-sm text-zinc-700">{l}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-zinc-400 mt-16 tracking-[0.1em] uppercase">
          © {new Date().getFullYear()} {store.name} — Powered by HostPoint Retail
        </p>
      </footer>

      <BagDrawer code={code} symbol={store.currency_symbol} />
    </div>
  );
}
